import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnInit,
} from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { Product } from '../../../shared/models/catalog/product.model';
import { CategoryService } from '../../../shared/services/catalog/category.service';
import { ProductService } from '../../../shared/services/catalog/product.service';

@Component({
  selector: 'app-products-page',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    RouterModule,
    TranslateModule,
    CardModule,
    TableModule,
    ButtonModule,
    TagModule,
    ToastModule,
    ConfirmDialogModule,
  ],
  providers: [MessageService, ConfirmationService],
  templateUrl: './products-page.component.html',
  styleUrl: './products-page.component.scss',
})
export class ProductsPageComponent implements OnInit {
  service = inject(ProductService);
  categoryService = inject(CategoryService);
  #messageService = inject(MessageService);
  #confirmationService = inject(ConfirmationService);
  #translate = inject(TranslateService);
  #router = inject(Router);

  categoryLookup = computed(() => {
    const map = new Map<number, string>();
    for (const c of this.categoryService.categories()) {
      map.set(c.id, c.title);
    }
    return map;
  });

  ngOnInit(): void {
    this.service.getAll();
    this.categoryService.getCategories();
  }

  navigateToCreate() {
    this.#router.navigate(['/catalog/products/new']);
  }

  navigateToDetail(item: Product) {
    this.#router.navigate(['/catalog/products', item.id]);
  }

  productTitle(item: Product): string {
    return item.detail?.title ?? item.key;
  }

  categoryTitle(id: number): string {
    return this.categoryLookup().get(id) ?? `#${id}`;
  }

  formatDisplayPrice(item: Product): string {
    if (item.display_price === null || item.display_price === undefined) return '—';
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
    }).format(item.display_price);
  }

  onRowDelete(event: Event, item: Product) {
    this.#confirmationService.confirm({
      target: event.target as EventTarget,
      header: this.#translate.instant('catalog.products.deleteConfirmHeader'),
      message: this.#translate.instant('catalog.products.deleteConfirmMessage'),
      acceptLabel: this.#translate.instant('label.delete'),
      rejectLabel: this.#translate.instant('label.cancel'),
      acceptButtonProps: { severity: 'danger' },
      accept: () => {
        this.service.delete(item.id).subscribe({
          next: () => {
            this.service.getAll();
            this.#messageService.add({
              severity: 'success',
              summary: this.#translate.instant('label.successHeader'),
              detail: this.#translate.instant('catalog.products.deleted'),
            });
          },
          error: () => {
            this.#messageService.add({
              severity: 'error',
              summary: this.#translate.instant('label.errorHeader'),
              detail: this.#translate.instant('catalog.products.deleteError'),
            });
          },
        });
      },
    });
  }
}
